import { formatAccounting, formatAccountingDeduction } from '@/lib/currency';
import { SummaryList, SummaryRow } from './SummaryList';
import type { CommissionDetailDto } from '../types';

interface EstimatorInfoPanelProps {
  commission: CommissionDetailDto;
  /** Total customer payments collected on this job. */
  collected: number;
  /** Total job costs, tax included. */
  costs: number;
}

/**
 * The "Collected / Costs / Balance / Commission" summary the client uses to
 * read a job at a glance. Restores the legacy Estimator Information panel
 * (JobCost.aspx:788-822, JobCost.aspx.cs:369-396), which had gone missing from
 * the rebuilt page (Angie, Note 3).
 *
 * Field mapping, verified against the legacy calculation:
 *
 *   Collected  = totalPayment                     -> paymentTotal
 *   Costs      = totalCost (incl. tax)            -> costTotals.grandTotal
 *   Balance    = totalPayment - totalCost         -> collected - costs
 *   Commission = Balance x estimatorPercent       -> commission.commissionAmount
 *
 * The Commission row deliberately uses the server-computed amount rather than
 * recomputing `balance * rate` here. The backend already applies exactly the
 * legacy formula — `Profit = TotalPaymentsReceived - TotalCosts` and
 * `CommissionAmount = Profit x (CommissionRate / 100)`
 * (CommissionCalculationService.cs:58,166) — and commission arithmetic must
 * have a single source of truth on the server, not a second implementation in
 * the UI that can drift from it.
 *
 * Costs renders as a parenthesised deduction to match the legacy `-$#,##0.00`
 * mask, so the four rows read as a subtraction down the column.
 */
export function EstimatorInfoPanel({ commission, collected, costs }: EstimatorInfoPanelProps) {
  const balance = collected - costs;

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="px-2 py-1.5 border-b border-border bg-gray-50 rounded-t-lg">
        <h4 className="text-xs font-semibold">{commission.personName}</h4>
      </div>
      <SummaryList className="p-2">
        <SummaryRow label="Collected" value={formatAccounting(collected)} />
        <SummaryRow label="Costs" value={formatAccountingDeduction(costs)} />
        <SummaryRow label="Balance" value={formatAccounting(balance)} />
        <SummaryRow
          label="Commission"
          value={formatAccounting(commission.commissionAmount)}
          emphasis
        />
      </SummaryList>
    </div>
  );
}
